import { useMemo } from "react";
import moment from "moment-timezone";
import sampleData from "../../data/finFutAll.json";
import rules from "../../data/cftcRules.json";
import { evaluateRules } from "../SideDrawer/cftcRulesEngine.ts";
import { computePercentiles, computeZScores } from "../SideDrawer/positionView.js";
import {
  type CftcRegimeSnapshot,
  isSnapshotStale,
  mapIntradaySymbolToCftcKey,
} from "./cftcRegimeUtils.ts";

const CACHE_TTL_MS = 60 * 1000;
const snapshotCache = new Map<
  string,
  { timestamp: number; snapshot: CftcRegimeSnapshot | null }
>();

const parseAsOfDate = (row: any) => {
  const report =
    row?.Report_Date_as_YYYY_MM_DD || row?.["Report_Date_as_YYYY-MM-DD"];
  if (report) {
    return String(report);
  }
  const legacy = row?.As_of_Date_In_Form_YYMMDD;
  if (legacy) {
    const parsed = moment(String(legacy), "YYMMDD", true);
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM-DD");
    }
  }
  return null;
};

const buildSnapshot = (assetKey: string): CftcRegimeSnapshot | null => {
  const rows = (sampleData as any[]).filter(
    (row) => row?.Market_and_Exchange_Names === assetKey
  );
  if (!rows.length) {
    return null;
  }
  const sorted = rows
    .map((row) => ({ row, date: parseAsOfDate(row) }))
    .filter((entry) => entry.date)
    .sort(
      (a, b) => Date.parse(String(a.date)) - Date.parse(String(b.date))
    );
  if (!sorted.length) {
    return null;
  }
  const latestIndex = sorted.length - 1;
  const latestRow = sorted[latestIndex].row;
  const latestDate = sorted[latestIndex].date as string;

  const levNetSeries = sorted.map(
    ({ row }) =>
      Number(row?.Lev_Money_Positions_Long_All ?? 0) -
      Number(row?.Lev_Money_Positions_Short_All ?? 0)
  );
  const assetNetSeries = sorted.map(
    ({ row }) =>
      Number(row?.Asset_Mgr_Positions_Long_All ?? 0) -
      Number(row?.Asset_Mgr_Positions_Short_All ?? 0)
  );
  const dealerNetSeries = sorted.map(
    ({ row }) =>
      Number(row?.Dealer_Positions_Long_All ?? 0) -
      Number(row?.Dealer_Positions_Short_All ?? 0)
  );
  const openInterestSeries = sorted.map(
    ({ row }) => Number(row?.Open_Interest_All ?? 0)
  );

  const levNetPercentiles = computePercentiles(levNetSeries);
  const amNetPercentiles = computePercentiles(assetNetSeries);
  const dealerNetPercentiles = computePercentiles(dealerNetSeries);
  const levNetZScores = computeZScores(levNetSeries);

  const latestOi = openInterestSeries[latestIndex] || null;
  const oiChange4w =
    latestIndex >= 4 && Number.isFinite(openInterestSeries[latestIndex - 4])
      ? latestOi - openInterestSeries[latestIndex - 4]
      : null;

  const latestLevNet = levNetSeries[latestIndex];
  const latestAmNet = assetNetSeries[latestIndex];
  const latestDealerNet = dealerNetSeries[latestIndex];

  const latestLevNetPct =
    latestOi && latestOi !== 0 ? (latestLevNet / latestOi) * 100 : null;
  const latestAmNetPct =
    latestOi && latestOi !== 0 ? (latestAmNet / latestOi) * 100 : null;
  const latestDealerNetPct =
    latestOi && latestOi !== 0 ? (latestDealerNet / latestOi) * 100 : null;

  const context = {
    LF_net: latestLevNet,
    AM_net: latestAmNet,
    Dealer_net: latestDealerNet,
    LF_netPctOI: latestLevNetPct,
    AM_netPctOI: latestAmNetPct,
    Dealer_netPctOI: latestDealerNetPct,
    LF_net_z: levNetZScores[latestIndex],
    LF_net_percentile: levNetPercentiles[latestIndex],
    AM_net_percentile: amNetPercentiles[latestIndex],
    Dealer_net_percentile: dealerNetPercentiles[latestIndex],
    LF_deltaNet_1w:
      latestIndex >= 1 ? latestLevNet - levNetSeries[latestIndex - 1] : null,
    LF_deltaNet_4w:
      latestIndex >= 4 ? latestLevNet - levNetSeries[latestIndex - 4] : null,
    OI: latestOi,
    OI_change_4w: oiChange4w,
  };

  const evaluation = evaluateRules(context, rules as any, "FX", {
    now: Date.now(),
  });
  const badgeLabel = evaluation.badge?.label || "Neutral";
  const normalizedLabel =
    badgeLabel === "Macro-Led / Uncorrelated"
      ? "Macro-Led"
      : badgeLabel === "Flow-Driven"
      ? "Flow-Driven"
      : badgeLabel === "Squeeze Watch"
      ? "Squeeze Watch"
      : "Neutral";
  const macroBias =
    latestAmNet > 0
      ? "Bullish"
      : latestAmNet < 0
      ? "Bearish"
      : "Neutral";
  const confidence =
    normalizedLabel === "Squeeze Watch"
      ? "High"
      : macroBias === "Neutral"
      ? "Low"
      : "Med";

  return {
    assetKey,
    asOfDate: latestDate,
    regimeLabel: normalizedLabel as CftcRegimeSnapshot["regimeLabel"],
    macroBias,
    confidence,
    notes: ["Derived from latest CFTC snapshot in local dataset."],
  };
};

export const useCftcRegimeSnapshot = (symbol: string) => {
  const assetKey = mapIntradaySymbolToCftcKey(symbol);

  const snapshot = useMemo(() => {
    if (!assetKey) {
      return null;
    }
    const cached = snapshotCache.get(assetKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.snapshot;
    }
    const computed = buildSnapshot(assetKey);
    snapshotCache.set(assetKey, { timestamp: Date.now(), snapshot: computed });
    return computed;
  }, [assetKey]);

  const stale = useMemo(() => {
    if (!snapshot?.asOfDate) return false;
    return isSnapshotStale(snapshot.asOfDate, Date.now());
  }, [snapshot?.asOfDate]);

  return {
    assetKey,
    snapshot,
    stale,
  };
};
